import { computed, observable } from "mobx";

import { IVector2 } from "@formant/core/model/IVector2";

import { ITeleopSession } from "../teleop/ITeleopSession";
import { IOperateStore } from "./IOperateStore";
import { shareTeleopDeviceIdKey } from "./ShareTeleopDeviceIdKey";

export class OperateStore implements IOperateStore {
    @observable
    armSwitchEnabled: boolean = false;

    @observable
    tryingToInteract: boolean = false;

    @observable
    session?: ITeleopSession;

    @observable
    usingTerminal: boolean = false;

    @observable
    onboardingShownTerminal: boolean = false;

    @observable
    showHelp: boolean = false;

    @observable
    metricsVisible: boolean = false;

    @observable
    teleopPrimarySwapIndex: number = 0;

    @observable
    feedbackVisible: boolean = false;

    @observable
    fill?: boolean;

    @observable
    canFill?: boolean;

    @observable.struct
    contentDimensions?: IVector2;

    @observable.struct
    containerDimensions?: IVector2;

    @observable.struct
    controlsDimensions?: IVector2;

    @observable
    editingDevice: boolean = false;

    @observable
    gamepad?: Gamepad | null;

    constructor() {
        this.gamepad = navigator.getGamepads?.().find(_ => _ !== undefined);
        window.addEventListener("gamepadconnected", (ev: GamepadEvent) => {
            this.gamepad = ev.gamepad;
            this.refreshGamepad();
        });
        window.addEventListener(
            "gamepaddisconnected",
            () => (this.gamepad = undefined),
        );
    }

    private refreshGamepad = () => {
        const f = () => {
            if (this.gamepad) {
                this.gamepad = navigator.getGamepads?.()[this.gamepad.index];
                window.requestAnimationFrame(f);
            }
        };
        f();
    };

    @computed
    get share(): string | undefined {
        return localStorage.getItem(shareTeleopDeviceIdKey) || undefined;
    }

    @computed
    get showArmSwitch(): boolean {
        const configuration =
            this.session?.deviceConfiguration?.document.teleop?.armSwitch;

        return configuration !== false;
    }

    @computed
    get allowLowBandwidthMode(): boolean {
        const configuration =
            this.session?.deviceConfiguration?.document.teleop
                ?.allowLowBandwidthMode;

        return !!configuration;
    }

    @computed
    get armed(): boolean {
        const { armSwitchEnabled, showArmSwitch } = this;

        return (showArmSwitch && armSwitchEnabled) || showArmSwitch === false;
    }

    @computed
    get shouldAutoFill(): boolean {
        const autoFillThreshold = 0.5;
        const containerAspect = aspect(this.containerDimensions);
        const contentAspect = aspect(this.contentDimensions);

        return Math.abs(containerAspect - contentAspect) < autoFillThreshold;
    }
}

function aspect(dimensions?: IVector2): number {
    if (!dimensions) {
        return 0;
    }
    const { x, y } = dimensions;
    if (x <= 0 || y <= 0) {
        return 0;
    }
    return x / y;
}
